/**
 * A lista de contas que compõem saldo, dentro de "Ver detalhes".
 *
 * PEDIDO DO USUÁRIO (09/10/2026): "em ver detalhes acrescente uma lista para
 * mostrar todas as contas (com um botão de ver mais para paginar) e a
 * porcentagem de conciliação de cada conta".
 *
 * A linha segue a MESMA linguagem da faixa (valor curto + barra + %), decisão
 * do usuário: a versão com todas as colunas voltaria a empilhar números, que é
 * o problema que acabamos de corrigir.
 *
 * Funções puras: o componente só escolhe cor e posição.
 */

import { IContaDetalhe, ILadoComposicao } from './conta/papelTrabalho';
import { pctConciliado } from './lados';

/** Quantas contas aparecem antes do "Ver mais". */
export const CONTAS_POR_PAGINA = 5;

/** Reexporta o tipo do contrato: uma definição só, no `papelTrabalho`. */
export type IContaComposicao = IContaDetalhe;
/**
 * A ordem da lista: MAIOR movimento primeiro.
 *
 * Ordenar por código de conta colocaria a conta 16 (R$ 31,7 mi) ao lado da 25
 * (R$ 0,00) só porque os números são próximos. Quem abre a lista quer ver onde
 * está o dinheiro.
 *
 * O critério é `provisionado + sem_par`, não só o provisionado: uma conta com
 * zero de título e R$ 73,9 mi de sem par (a 1142/16, medida) é exatamente a que
 * precisa aparecer primeiro.
 */
export const ordenarContas = (cs: IContaComposicao[] | undefined): IContaComposicao[] =>
  [...(cs ?? [])].sort(
    (a, b) =>
      Number(b.provisionado ?? 0) +
      Number(b.sem_par ?? 0) -
      (Number(a.provisionado ?? 0) + Number(a.sem_par ?? 0)),
  );

/**
 * A fatia visível da lista.
 *
 * `paginas` começa em 1 e cresce a cada "Ver mais" — o botão ACUMULA em vez de
 * trocar de página: numa lista curta, perder as linhas já lidas para ver as
 * próximas atrapalha a comparação, que é justamente o que a lista serve.
 */
export const contasVisiveis = (
  cs: IContaComposicao[] | undefined,
  paginas: number,
): IContaComposicao[] => ordenarContas(cs).slice(0, Math.max(1, paginas) * CONTAS_POR_PAGINA);

/** Quantas contas ainda faltam aparecer. */
export const contasRestantes = (cs: IContaComposicao[] | undefined, paginas: number): number =>
  Math.max(0, (cs?.length ?? 0) - contasVisiveis(cs, paginas).length);

/**
 * O rótulo da conta: código + nome.
 *
 * Só o código não identifica (o mesmo "16" é Clientes numa empresa e outra
 * coisa em outra — o plano de contas muda por empresa); só o nome não permite
 * achar no ERP.
 */
export const rotuloConta = (c: IContaComposicao): string =>
  [c.conta, c.conta_nome].filter(Boolean).join(' ') || '—';

/**
 * O % de conciliação da conta — o que o usuário pediu explicitamente.
 *
 * É `conciliado / provisionado`: quanto do título já foi ligado a um pagamento.
 * NÃO é o `pct_casado`, que mede outra coisa (quanto do movimento tem par,
 * descontando o que não tem par por natureza). Os dois aparecem, com rótulos
 * distintos, porque respondem perguntas diferentes.
 */
export const pctDaConta = (c: IContaComposicao): number | null =>
  pctConciliado(Number(c.conciliado ?? 0), Number(c.provisionado ?? 0));

/**
 * `true` quando a conta não tem movimento nenhum.
 *
 * Conta zerada entra na contagem (ela existe no plano), mas a tela a mostra
 * apagada: uma barra vazia com 0% ao lado de contas ativas sugere falha de
 * conciliação onde não há o que conciliar.
 */
export const contaSemMovimento = (c: IContaComposicao): boolean =>
  Number(c.provisionado ?? 0) === 0 && Number(c.sem_par ?? 0) === 0 && Number(c.titulos ?? 0) === 0;

/** Converte a conta para o formato que a barra da faixa já sabe desenhar. */
export const contaComoLado = (c: IContaComposicao): Omit<ILadoComposicao, 'lado'> =>
  ({
    contas: 1,
    saldo_razao: 0,
    provisionado: Number(c.provisionado ?? 0),
    em_aberto: Number(c.em_aberto ?? 0),
    conciliado: Number(c.conciliado ?? 0),
    sem_par: Number(c.sem_par ?? 0),
    sem_par_natureza: Number(c.sem_par_natureza ?? 0),
    sem_par_real: Number(c.sem_par_real ?? 0),
    titulos: Number(c.titulos ?? 0),
    titulos_ligados: Number(c.titulos_ligados ?? 0),
    pct_casado: c.pct_casado ?? null,
  }) as Omit<ILadoComposicao, 'lado'>;
